import PropTypes from 'prop-types'
import Dropdown from './Dropdown'
import FormFieldLabel from './FormFieldLabel'
import cn from '../modules/cn'
import TextInput from './TextInput'
import Toggle from './Toggle'
import Select from './Select'

function FormField({
  label,
  name,
  type,
  fullWidth,
  value,
  onChange,
  required,
  disabled,
  inputProps,
  children,
  className,
  errorMessage,
}) {
  return (
    <div
      className={cn(
        'col-span-6',
        !fullWidth && 'sm:col-span-4',
        className,
        disabled && 'opacity-30 cursor-not-allowed',
      )}
    >
      {label && (
        <FormFieldLabel name={name}>
          {label}
          {required && <span className="ml-1 text-red-300">*</span>}
        </FormFieldLabel>
      )}
      {(() => {
        switch (type) {
          case 'toggle':
            return (
              <Toggle
                label={label}
                {...inputProps /* eslint-disable-line react/jsx-props-no-spreading */}
                name={name}
                onChange={onChange}
                disabled={disabled}
                className={cn(
                  errorMessage && 'border-red-600 hover:border-red-600',
                  disabled && 'pointer-events-none',
                )}
              />
            )
          case 'dropdown':
            return (
              <Dropdown
                label={inputProps.placeholder}
                {...inputProps} /* eslint-disable-line react/jsx-props-no-spreading */
                name={name}
                fullWidth
                disabled={disabled}
                buttonProps={{
                  inline: true,
                  ...inputProps.buttonProps,
                }}
              >
                {children}
              </Dropdown>
            )
          case 'select':
            return (
              <Select
                {...inputProps} /* eslint-disable-line react/jsx-props-no-spreading */
                className={cn(
                  errorMessage && 'border-red-600 hover:border-red-600',
                  disabled && 'pointer-events-none',
                )}
              >
                {children}
              </Select>
            )
          case 'textarea':
            return (
              <textarea
                {...{ /* eslint-disable-line react/jsx-props-no-spreading */
                  value: onChange ? value : undefined,
                  defaultValue: !onChange ? value : undefined,
                  onChange,
                  disabled,
                  ...inputProps,
                }}
                name={name}
                value={value}
                rows={7}
                className={cn(
                  'w-full border-0 border-b-2 border-gray-200 bg-transparent hover:bg-transparent hover:shadow-none',
                  'justify-between focus:ring-0 hover:border-gray-300 active:border-theme-500 focus:border-theme-500',
                  'p-0',
                  errorMessage && 'border-red-600 hover:border-red-600 focus:border-red-600 active:border-red-600',
                  disabled && 'pointer-events-none',
                )}
              />
            )
          default:
            return (
              <TextInput
                name={name}
                type={type}
                inline
                {...{ /* eslint-disable-line react/jsx-props-no-spreading */
                  value: onChange ? value : undefined,
                  defaultValue: !onChange ? value : undefined,
                  onChange,
                  disabled,
                  ...inputProps,
                }}
                className={cn(
                  errorMessage && 'border-red-600 hover:border-red-600 focus:border-red-600 active:border-red-600',
                  disabled && 'pointer-events-none',
                )}
              />
            )
        }
      })()}
      {errorMessage && <div className="text-red-600 text-xs pt-2">{errorMessage}</div>}
    </div>
  )
}

FormField.propTypes = {
  label: PropTypes.node,
  name: PropTypes.string.isRequired,
  type: PropTypes.oneOfType([PropTypes.oneOf(['text', 'toggle', 'dropdown']), PropTypes.string]),
  inputProps: PropTypes.shape(),
  onChange: PropTypes.func,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fullWidth: PropTypes.bool,
  children: PropTypes.node,
  className: cn.propType,
  errorMessage: PropTypes.string,
}

FormField.defaultProps = {
  label: null,
  type: 'text',
  inputProps: {},
  fullWidth: false,
  onChange: null,
  required: false,
  disabled: false,
  value: '',
  children: null,
  className: null,
  errorMessage: null,
}

export default FormField
